import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const SITE = 'https://lorescout.com';
const URL = `${SITE}/guides/broom-deck`;
const TITLE = 'Lorcana Broom Deck Guide — Mickey & Magic Brooms · LoreScout';
const DESCRIPTION =
  'Complete Disney Lorcana Broom deck guide: 60-card decklist, key card synergies between Mickey Mouse - Wayward Sorcerer and Magic Broom, mulligan plan, and matchup tips.';

interface CardEntry {
  qty: number;
  name: string;
  subtitle: string;
  ink: string;
}

const DECKLIST: CardEntry[] = [
  { qty: 4, name: 'Mickey Mouse', subtitle: 'Wayward Sorcerer', ink: 'Amethyst' },
  { qty: 4, name: 'Magic Broom', subtitle: 'Bucket Brigade', ink: 'Amethyst' },
  { qty: 4, name: 'Magic Broom', subtitle: 'Broom Closet', ink: 'Amethyst' },
  { qty: 4, name: 'Magic Broom', subtitle: 'Illuminated Servant', ink: 'Amethyst' },
  { qty: 4, name: 'Yzma', subtitle: 'Alchemist', ink: 'Amethyst' },
  { qty: 3, name: 'Diablo', subtitle: 'Devoted Raven', ink: 'Amethyst' },
  { qty: 2, name: 'Sisu', subtitle: 'Divine Water Dragon', ink: 'Amethyst' },
  { qty: 4, name: 'Dragon Fire', subtitle: 'Action', ink: 'Ruby' },
  { qty: 4, name: 'Smash', subtitle: 'Action', ink: 'Ruby' },
  { qty: 3, name: 'Goofy', subtitle: 'Daredevil', ink: 'Ruby' },
  { qty: 4, name: 'Maui', subtitle: 'Hero to All', ink: 'Ruby' },
  { qty: 2, name: 'Stitch', subtitle: 'Rock Star', ink: 'Ruby' },
  { qty: 4, name: 'Tipo', subtitle: 'Growing Son', ink: 'Amethyst' },
  { qty: 4, name: 'Pawpsicle', subtitle: 'Item', ink: 'Ruby' },
  { qty: 4, name: 'Develop Your Brain', subtitle: 'Song', ink: 'Amethyst' },
  { qty: 2, name: 'Be Prepared', subtitle: 'Song', ink: 'Ruby' },
  { qty: 4, name: 'Friends on the Other Side', subtitle: 'Song', ink: 'Amethyst' },
];

const totalCards = DECKLIST.reduce((s, c) => s + c.qty, 0);

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to Build a Disney Lorcana Broom Deck',
  description: DESCRIPTION,
  totalTime: 'PT15M',
  url: URL,
  step: [
    { '@type': 'HowToStep', name: 'Pick your inks', text: 'Amethyst gives you the Broom shells and Mickey Mouse - Wayward Sorcerer; Ruby adds removal and Maui as a finisher.' },
    { '@type': 'HowToStep', name: 'Build the Broom engine', text: 'Run 4 copies each of Magic Broom - Bucket Brigade, Broom Closet, and Illuminated Servant. They become free with Mickey on the board.' },
    { '@type': 'HowToStep', name: 'Add card draw and removal', text: 'Develop Your Brain and Friends on the Other Side dig for Mickey; Dragon Fire and Smash clear key threats like Tinker Bell or Beast.' },
    { '@type': 'HowToStep', name: 'Close the game', text: 'Use Maui and a swarm of evasive or ready Brooms to push lethal once your board snowballs.' },
  ],
};

const faqLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What inks should I use for a Lorcana Broom deck?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Amethyst/Ruby is the default. Amethyst provides Mickey Mouse - Wayward Sorcerer and the Magic Broom cards; Ruby brings removal (Dragon Fire, Smash) and a big finisher in Maui - Hero to All.',
      },
    },
    {
      '@type': 'Question',
      name: 'Why is Mickey Mouse - Wayward Sorcerer so important?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'His ability lets you play Magic Broom characters for free, turning every Broom in your deck into a tempo-positive play and enabling explosive multi-Broom turns.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I mulligan a Broom deck?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Keep one Mickey Mouse - Wayward Sorcerer or any cheap inkable. Toss expensive finishers like Maui on the play and removal you cannot cast yet.',
      },
    },
  ],
};

export default function BroomDeckGuidePage() {
  return (
    <div className="app-container min-h-screen pb-12">
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={URL} />
        <meta property="og:title" content="Disney Lorcana Broom Deck — Strategy Guide" />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content={URL} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Disney Lorcana Broom Deck — Strategy Guide" />
        <meta name="twitter:description" content={DESCRIPTION} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </Helmet>

      <header className="flex items-center gap-3 p-4 border-b border-border">
        <Link to="/" className="text-sm underline">← LoreScout</Link>
      </header>

      <article className="px-4 pt-4">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link>
          {' / '}<span className="text-foreground">Guides</span>
          {' / '}<span className="text-foreground">Broom Deck</span>
        </nav>

        <h1 className="font-serif text-3xl">Disney Lorcana Broom Deck Guide</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          A complete guide to building, mulliganing, and piloting the Amethyst/Ruby <strong>Magic Broom</strong> deck
          powered by <em>Mickey Mouse — Wayward Sorcerer</em>.
        </p>

        <section className="mt-6">
          <h2 className="font-serif text-2xl">Why the Broom deck works</h2>
          <p className="mt-2 text-sm leading-relaxed">
            The deck is built around a single combo: <strong>Mickey Mouse — Wayward Sorcerer</strong> lets you play
            Magic Broom characters for free. With three different Brooms running four copies each, almost every draw
            becomes a free body once Mickey resolves. The result is a board that snowballs faster than control decks
            can answer, while staying inkable enough to power your own removal and song package.
          </p>
        </section>

        <section className="mt-8">
          <h2 className="font-serif text-2xl">Key card interactions</h2>
          <ul className="mt-3 space-y-2 text-sm">
            <li><strong>Mickey Mouse — Wayward Sorcerer + Magic Broom — Bucket Brigade:</strong> a 3-cost Mickey turn into a free 1-cost Broom keeps your ink curve open for songs.</li>
            <li><strong>Magic Broom — Illuminated Servant + Develop Your Brain:</strong> Servant draws cards on quest, and Develop Your Brain refills your hand to keep the Broom chain going.</li>
            <li><strong>Yzma — Alchemist:</strong> singing <em>Friends on the Other Side</em> for free turns into a discount engine that fuels the late game.</li>
            <li><strong>Dragon Fire + Smash:</strong> hard removal for opposing 5-cost+ threats so your Brooms can keep questing.</li>
            <li><strong>Maui — Hero to All:</strong> evasive 7-drop that closes games once Brooms have stalled the opponent.</li>
          </ul>
        </section>

        <section className="mt-8">
          <h2 className="font-serif text-2xl">Sample decklist ({totalCards} cards)</h2>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {DECKLIST.map((c) => (
              <div key={`${c.name}-${c.subtitle}`} className="flex items-center justify-between rounded-md border border-border bg-card/50 px-3 py-2 text-sm">
                <span>
                  <span className="font-medium tabular-nums mr-2">{c.qty}×</span>
                  <span className="font-medium">{c.name}</span>
                  <span className="text-xs text-muted-foreground ml-2 italic">{c.subtitle}</span>
                </span>
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{c.ink}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-8">
          <h2 className="font-serif text-2xl">Mulligan guide</h2>
          <p className="mt-2 text-sm leading-relaxed">
            On the play, keep any hand with a <strong>Mickey Mouse — Wayward Sorcerer</strong> and at least one
            inkable 1- or 2-cost card. On the draw, prioritize early interaction (Pawpsicle, a cheap Broom, or
            Develop Your Brain) so you survive into your combo turn on 3. Always toss <em>Maui</em>, <em>Sisu</em>,
            and uncastable removal in the opening hand — they are dead weight before turn 4.
          </p>
        </section>

        <section className="mt-8">
          <h2 className="font-serif text-2xl">Matchup tips</h2>
          <ul className="mt-3 space-y-2 text-sm">
            <li><strong>Amber/Steel control:</strong> hold Dragon Fire for Tinker Bell — Giant Fairy; chain Brooms before they stabilize.</li>
            <li><strong>Ruby/Sapphire item decks:</strong> race with evasive Brooms and save Smash for Pawpsicle-fueled finishers.</li>
            <li><strong>Mirror:</strong> whoever lands Mickey first usually wins. Mulligan aggressively for him and protect him with songs.</li>
          </ul>
        </section>

        <section className="mt-10 rounded-lg border border-border p-4 text-sm text-muted-foreground">
          <p>
            See also: <Link to="/rotation" className="underline">Lorcana rotation</Link> ·{' '}
            <Link to="/banned" className="underline">banned cards</Link> ·{' '}
            <Link to="/sets" className="underline">all sets</Link>.
          </p>
        </section>
      </article>
    </div>
  );
}
